import { computed, ref, type Ref } from 'vue';
import type { FacilityStatus } from '../../tri-shared/types/tri';
import { poundsNumberFormat } from '../../tri-shared/services/formatting';
import type { RouteNamedMap } from '@/router';
import { actions } from '@/services/actions';
import { alertModal } from '../../cross-app-frontend/modals/AlertModal.vue';

export type FacilityWorkflowRouteName =
  | 'Facility Products'
  | 'Facility Usage'
  | 'Facility Thresholds'
  | 'Facility Release Profiles'
  | 'Facility Questionnaire'
  | 'Facility Form R'
  | 'Facility Form R Submit';

export interface FacilityWorkflowStatusAndNavigationItem {
  stage: string;
  isDone: boolean;
  briefDetails: string;
  extraDetails?: string;
  icon: `fa-${string}`;
  route: FacilityWorkflowRouteName;
  actions: ({
    label: string;
    icon: `fa-${string}`;
    variant: 'default' | 'outline' | 'ghost';
    isShowing: boolean;
  } & (
    | {
        func: () => unknown;
      }
    | { route: keyof RouteNamedMap }
  ))[];
}

interface PermissionFilter {
  hasUpdateBaseFacts: boolean;
  hasUpdateFacility: boolean;
  hasAdministerFacility: boolean;
}

export function useFacilityWorkflowStatusAndNavigation(
  statusLoadedRef: Ref<FacilityStatus | null>,
  permissionsRef: Ref<PermissionFilter | null>,
): Ref<FacilityWorkflowStatusAndNavigationItem[]> {
  const awaitingBackend = ref(false);

  return computed((): FacilityWorkflowStatusAndNavigationItem[] => {
    const status = statusLoadedRef.value;
    if (!status) {
      return [];
    }

    const permissions = permissionsRef?.value;
    const hasUpdateBaseFacts = permissions?.hasUpdateBaseFacts ?? false;
    const hasUpdateFacility = permissions?.hasUpdateFacility ?? false;
    const hasAdministerFacility = permissions?.hasAdministerFacility ?? false;

    const isDataPreparer = hasUpdateBaseFacts && !hasUpdateFacility && !hasAdministerFacility;

    const prodCount = status.productsAdded ?? 0;
    const verb = prodCount === 1 ? 'has' : 'have';
    const plural = prodCount === 1 ? '' : 's';
    const isDoneWithFinalReview =
      status.formRSubmissionStatus === 'ready-to-submit' ||
      status.formRSubmissionStatus === 'submitted' ||
      status.formRSubmissionStatus === 'certified';
    const hasGeneratedFormR =
      status.formRSubmissionStatus === 'submitted' || status.formRSubmissionStatus === 'certified';

    const thresholdExceedanceCount = status.thresholdsExceeded.length;

    const hasThresholdExceedancesOrPendingWork =
      !status.isDoneAddingProducts ||
      !status.isDoneDefiningActivity ||
      !status.isDoneReviewingThresholds ||
      thresholdExceedanceCount > 0;

    const result: FacilityWorkflowStatusAndNavigationItem[] = [
      {
        stage: 'Add products',
        isDone: status.isDoneAddingProducts,
        briefDetails: `${prodCount} product${plural} ${verb} been added.`,
        icon: 'fa-boxes-stacked',
        route: 'Facility Products',
        actions: [
          {
            label: 'Done adding products',
            icon: 'fa-check',
            variant: 'outline',
            isShowing: !awaitingBackend.value && !status.isDoneAddingProducts,
            func: async () => {
              try {
                awaitingBackend.value = true;
                await actions.facilities.statusUpdate({
                  facilityID: status.facilityID,
                  year: status.year,
                  isDoneAddingProducts: true,
                });
                statusLoadedRef.value!.isDoneAddingProducts = true;
              } catch (e: any) {
                console.error(e);
                await alertModal({
                  title: 'Unable to update status',
                  message: `An unexpected error occurred and we were unable to save your status update. Error was: ${e.message}`,
                });
              } finally {
                awaitingBackend.value = false;
              }
            },
          },
        ],
      },
      {
        stage: 'Define activity levels',
        isDone: status.thresholdPoundsTotal > 0 && status.isDoneDefiningActivity,
        briefDetails: `${poundsNumberFormat.format(status.thresholdPoundsTotal)} lbs reported.`,
        icon: 'fa-database',
        route: 'Facility Usage',
        actions: [
          {
            label: 'Review Activity',
            icon: 'fa-file-chart-column',
            variant: 'outline',
            isShowing: status.thresholdPoundsTotal > 0,
            route: 'Facility Activity Review',
          },
          {
            label: 'Done defining activity',
            icon: 'fa-check',
            variant: 'outline',
            isShowing: !awaitingBackend.value && status.thresholdPoundsTotal > 0 && !status.isDoneDefiningActivity,
            func: async () => {
              try {
                awaitingBackend.value = true;
                await actions.facilities.statusUpdate({
                  facilityID: status.facilityID,
                  year: status.year,
                  isDoneDefiningActivity: true,
                });
                statusLoadedRef.value!.isDoneDefiningActivity = true;
              } catch (e: any) {
                console.error(e);
                await alertModal({
                  title: 'Unable to update status',
                  message: `An unexpected error occurred and we were unable to save your status update. Error was: ${e.message}`,
                });
              } finally {
                awaitingBackend.value = false;
              }
            },
          },
        ],
      },
      {
        stage: 'Review thresholds',
        isDone: status.isDoneReviewingThresholds,
        icon: 'fa-magnifying-glass-chart',
        briefDetails: `Confirm ${thresholdExceedanceCount} exceedance${thresholdExceedanceCount === 1 ? '' : 's'}.`,
        extraDetails:
          status.isDoneAddingProducts && status.isDoneDefiningActivity
            ? `Now that products and activity levels have been added, you can review ${thresholdExceedanceCount} threshold exceedance${thresholdExceedanceCount === 1 ? '' : 's'}.`
            : 'Please add products and define activity levels before reviewing thresholds.',
        route: 'Facility Thresholds',
        actions: [
          {
            label: 'Review Exemptions',
            icon: 'fa-filter',
            variant: 'outline',
            isShowing: true,
            route: 'Facility Exemption Review',
          },
        ],
      },
    ];

    if (hasThresholdExceedancesOrPendingWork) {
      if (isDataPreparer) {
        result.push({
          stage: 'Answer questions',
          isDone: status.isDoneAnsweringQuestions,
          icon: 'fa-question-circle',
          briefDetails: 'For calculating releases.',
          extraDetails: 'Answer questions for calculating releases.',
          route: 'Facility Questionnaire',
          actions: [],
        });
      } else {
        const stagesToAdd: FacilityWorkflowStatusAndNavigationItem[] = [
          {
            stage: 'Assign release profiles',
            isDone: status.isDoneAssigningReleaseProfiles,
            icon: 'fa-screwdriver-wrench',
            briefDetails: 'Match calculations with operations.',
            extraDetails:
              status.isDoneAddingProducts && status.isDoneDefiningActivity
                ? 'Now that products and activity levels have been added, you can assign release profiles.'
                : 'Please add products and define activity levels before assigning release profiles.',
            route: 'Facility Release Profiles',
            actions: [],
          },
          {
            stage: 'Answer questions',
            isDone: status.isDoneAnsweringQuestions,
            icon: 'fa-question-circle',
            briefDetails: 'For calculating releases.',
            extraDetails: status.isDoneAssigningReleaseProfiles
              ? 'Now that release profiles have been assigned, you can answer questions.'
              : 'You must assign release profiles before you can answer questions.',
            route: 'Facility Questionnaire',
            actions: [],
          },
          {
            stage: 'Final review',
            isDone: isDoneWithFinalReview,
            icon: 'fa-check-circle',
            briefDetails: 'Confirm quality before submission.',
            extraDetails:
              status.isDoneAnsweringQuestions && status.isDoneReviewingReleases
                ? 'Now that questions have been answered and releases reviewed, you may now review the final TRI data.'
                : 'You must answer questions before you can review the final TRI data.',
            route: 'Facility Form R',
            actions: [],
          },
        ];

        if (hasAdministerFacility) {
          stagesToAdd.push({
            stage: 'Generate & submit Form R',
            isDone: hasGeneratedFormR,
            icon: 'fa-file-alt',
            briefDetails: 'For upload to TRI-MEweb.',
            extraDetails:
              status.formRSubmissionStatus !== 'under-review'
                ? 'Form R is now available for submission to the EPA.'
                : 'You must complete all prior steps before you can generate Form R.',
            route: 'Facility Form R Submit',
            actions: [],
          });
        }

        result.push(...stagesToAdd);
      }
    }

    if (isDataPreparer) {
      return result.filter((stage) => stage.stage !== 'Review thresholds');
    }

    return result;
  });
}
